import React, { useMemo } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
    />
  );
}

export default function DialogCampanhaConsultaFatura({
  PaperComponent,
  open,
  onClose,
  faturaConsulta,
  setFaturaConsulta,
  resultados,
  loading,
  onConsultar,
}) {
  const columns = useMemo(
    () => [
      { Header: "Campanha", accessor: "campanha", Filter: TextFilter },
      { Header: "Código", accessor: "codigo_campanha", Filter: TextFilter },
      { Header: "Fabricante", accessor: "fabricante", Filter: TextFilter },
      {
        Header: "Conjuntos",
        accessor: "conjuntosdetetados",
        Filter: TextFilter,
        Cell: ({ value }) => Number(value || 0),
      },
      {
        Header: "A receber",
        accessor: "valor_a_receber",
        Filter: TextFilter,
        Cell: ({ value }) => Number(value || 0).toFixed(2),
      },
      { Header: "Estado", accessor: "estado", Filter: TextFilter },
      { Header: "Materiais reservados", accessor: "materiais_reservados", Filter: TextFilter },
    ],
    []
  );

  const table = useTable(
    {
      columns,
      data: Array.isArray(resultados) ? resultados : [],
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = table;

  const primeiraLinha = Array.isArray(resultados) && resultados.length > 0 ? resultados[0] : null;

  return (
    <Dialog PaperComponent={PaperComponent} open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
        Consultar benefícios por fatura
      </DialogTitle>

      <DialogContent>
        <MDBox display="flex" gap={1.5} mb={2} alignItems="center">
          <MDInput
            fullWidth
            placeholder="Número da fatura"
            value={faturaConsulta}
            onChange={(e) => setFaturaConsulta(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onConsultar();
            }}
          />

          <MDButton color="info" onClick={onConsultar}>
            Consultar
          </MDButton>
        </MDBox>

        {primeiraLinha && (
          <MDBox display="flex" gap={3} mb={2} flexWrap="wrap">
            <span style={{ fontSize: 13, color: "#344767" }}>
              <b>Cliente:</b> {primeiraLinha.cliente || primeiraLinha.refcliente || "-"}
            </span>

            <span style={{ fontSize: 13, color: "#344767" }}>
              <b>Data:</b>{" "}
              {primeiraLinha.faturadata ? String(primeiraLinha.faturadata).substring(0, 10) : "-"}
            </span>

            <span style={{ fontSize: 13, color: "#344767" }}>
              <b>Vendedor:</b> {primeiraLinha.vendedor || primeiraLinha.refvendedor || "-"}
            </span>
          </MDBox>
        )}

        {loading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="220px">
            <CircularProgress />
          </MDBox>
        ) : (
          <MDBox sx={{ overflowX: "auto", maxHeight: "55vh" }}>
            <table
              {...getTableProps()}
              style={{
                width: "100%",
                minWidth: 1250,
                borderCollapse: "collapse",
              }}
            >
              <thead>
                {headerGroups.map((hg) => (
                  <tr {...hg.getHeaderGroupProps()}>
                    {hg.headers.map((col) => (
                      <th
                        {...col.getHeaderProps(col.getSortByToggleProps())}
                        style={{
                          fontSize: 12,
                          padding: 6,
                          borderBottom: "1px solid #e0e0e0",
                          color: "#344767",
                          textAlign: ["conjuntosdetetados", "valor_a_receber"].includes(col.id)
                            ? "right"
                            : "left",
                          verticalAlign: "bottom",
                        }}
                      >
                        <div>
                          {col.render("Header")}
                          {col.isSorted ? (col.isSortedDesc ? " ▼" : " ▲") : null}
                        </div>

                        <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                          {col.canFilter ? col.render("Filter") : null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody {...getTableBodyProps()}>
                {rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      style={{
                        fontSize: 13,
                        padding: 18,
                        textAlign: "center",
                        color: "#7b809a",
                      }}
                    >
                      Sem resultados para esta fatura.
                    </td>
                  </tr>
                )}

                {rows.map((row) => {
                  prepareRow(row);

                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          style={{
                            fontSize: 12,
                            padding: 6,
                            borderTop: "1px solid #eee",
                            textAlign: ["conjuntosdetetados", "valor_a_receber"].includes(
                              cell.column.id
                            )
                              ? "right"
                              : "left",
                            verticalAlign: "top",
                          }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </MDBox>
        )}
      </DialogContent>

      <DialogActions>
        <MDButton color="secondary" onClick={onClose}>
          Fechar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
